import type { Instruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';

import type { UserStandardTreeNode } from '../../../utils/userStandardsTree';

import type { UUID } from '@/shared/types/userStandard';

/** The slice of a move event a drop produces; the caller adds `ownerId`/`oldParentId`. */
export interface TreeMovePayload {
  id: UUID;
  newParentId: UUID | null;
  beforeId: UUID | null;
  afterId: UUID | null;
}

/** Read-only views over the full library tree, built once per render in the provider. */
export interface TreeLookups {
  nodeMap: Map<UUID, UserStandardTreeNode>;
  /** A node's `parent_id` (null for top-level/library-root nodes). */
  parentOf: (id: UUID) => UUID | null;
  /** Sorted children of a parent (`null` = library roots). */
  childrenOf: (parentId: UUID | null) => UserStandardTreeNode[];
}

export function buildTreeLookups(roots: UserStandardTreeNode[]): TreeLookups {
  const nodeMap = new Map<UUID, UserStandardTreeNode>();
  const childrenMap = new Map<UUID | null, UserStandardTreeNode[]>();
  childrenMap.set(null, roots);

  const walk = (nodes: UserStandardTreeNode[]) => {
    for (const node of nodes) {
      nodeMap.set(node.id, node);
      childrenMap.set(node.id, node.children);
      walk(node.children);
    }
  };
  walk(roots);

  return {
    nodeMap,
    parentOf: (id) => nodeMap.get(id)?.parent_id ?? null,
    childrenOf: (parentId) => childrenMap.get(parentId) ?? [],
  };
}

/** Ids of every node beneath `rootId` (its descendants), used for the cycle guard. */
export function getDescendantIds(rootId: UUID, lookups: TreeLookups): Set<UUID> {
  const out = new Set<UUID>();
  const stack = [...lookups.childrenOf(rootId)];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    out.add(node.id);
    stack.push(...node.children);
  }
  return out;
}

/**
 * Ancestors of `targetId` within the scoped pane, ordered top→down and excluding the target itself.
 * Walking stops at `rootParentId` (the pane's top), so `chain[d]` is the ancestor rendered at level
 * `d` — which is what the hitbox's `desiredLevel` indexes.
 */
function paneAncestorChain(
  targetId: UUID,
  rootParentId: UUID | null,
  lookups: TreeLookups
): UserStandardTreeNode[] {
  const chain: UserStandardTreeNode[] = [];
  let parentId = lookups.parentOf(targetId);
  while (parentId !== null && parentId !== rootParentId) {
    const node = lookups.nodeMap.get(parentId);
    if (!node) break;
    chain.push(node);
    parentId = lookups.parentOf(parentId);
  }
  return chain.reverse();
}

/**
 * `beforeId` precedes the node, `afterId` follows it (server / `insertByPosition` convention).
 * Returns `null` when the computed slot is where the node already sits (a no-op move).
 */
function noopOrMove(
  draggedId: UUID,
  newParentId: UUID | null,
  beforeId: UUID | null,
  afterId: UUID | null,
  lookups: TreeLookups
): TreeMovePayload | null {
  const currentParent = lookups.parentOf(draggedId);
  if (newParentId === currentParent) {
    const siblings = lookups.childrenOf(currentParent);
    const idx = siblings.findIndex((s) => s.id === draggedId);
    if (idx >= 0) {
      const currentBefore = siblings[idx - 1]?.id ?? null;
      const currentAfter = siblings[idx + 1]?.id ?? null;
      if (currentBefore === beforeId && currentAfter === afterId) return null;
    }
  }
  return { id: draggedId, newParentId, beforeId, afterId };
}

/**
 * Translate a hitbox `Instruction` (against `targetNode`) into a single `moveUserStandard` event.
 * Sibling lists always have the dragged node removed first, so cross-parent drops compute the right
 * neighbours. Returns `null` for blocked instructions and no-op slots.
 */
export function instructionToMoveInput({
  instruction,
  draggedId,
  targetNode,
  lookups,
  rootParentId,
}: {
  instruction: Instruction;
  draggedId: UUID;
  targetNode: UserStandardTreeNode;
  lookups: TreeLookups;
  rootParentId: UUID | null;
}): TreeMovePayload | null {
  const targetId = targetNode.id;

  switch (instruction.type) {
    case 'instruction-blocked':
      return null;

    case 'make-child': {
      const newParentId = targetId;
      const children = lookups.childrenOf(newParentId).filter((c) => c.id !== draggedId);
      const beforeId = children[children.length - 1]?.id ?? null;
      return noopOrMove(draggedId, newParentId, beforeId, null, lookups);
    }

    case 'reorder-above':
    case 'reorder-below': {
      const newParentId = lookups.parentOf(targetId) ?? rootParentId;
      const siblings = lookups.childrenOf(newParentId).filter((s) => s.id !== draggedId);
      const targetIdx = siblings.findIndex((s) => s.id === targetId);
      if (targetIdx < 0) return null;
      const insertIdx = instruction.type === 'reorder-above' ? targetIdx : targetIdx + 1;
      const beforeId = siblings[insertIdx - 1]?.id ?? null;
      const afterId = siblings[insertIdx]?.id ?? null;
      return noopOrMove(draggedId, newParentId, beforeId, afterId, lookups);
    }

    case 'reparent': {
      const chain = paneAncestorChain(targetId, rootParentId, lookups);
      const ancestor = chain[instruction.desiredLevel];
      if (!ancestor) return null;
      const newParentId = lookups.parentOf(ancestor.id) ?? rootParentId;
      const siblings = lookups.childrenOf(newParentId).filter((s) => s.id !== draggedId);
      const ancestorIdx = siblings.findIndex((s) => s.id === ancestor.id);
      if (ancestorIdx < 0) return null;
      const beforeId = ancestor.id;
      const afterId = siblings[ancestorIdx + 1]?.id ?? null;
      return noopOrMove(draggedId, newParentId, beforeId, afterId, lookups);
    }

    default:
      return null;
  }
}
